import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";

/**
 * DateRangePicker — shared selector with 9 presets + custom range.
 *
 * Props:
 *   value:    { preset: string, from?: string (yyyy-MM-dd), to?: string (yyyy-MM-dd) }
 *   onChange: (newValue) => void
 *   className: string (optional)
 *
 * Emits the canonical shape used by the reports APIs:
 *   { preset, from, to }
 *
 * For all non-custom presets, `from` and `to` are NOT sent (the backend
 * resolves them). For `custom`, `from` and `to` are required (yyyy-MM-dd).
 */

const PRESETS = [
  { value: "today", label: "Today" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "current_month", label: "Current month" },
  { value: "last_month", label: "Last month" },
  { value: "current_quarter", label: "Current quarter" },
  { value: "last_quarter", label: "Last quarter" },
  { value: "current_year", label: "Current year" },
  { value: "last_year", label: "Last year" },
  { value: "custom", label: "Custom…" },
];

const formatLabel = (preset, from, to) => {
  const p = PRESETS.find((x) => x.value === preset);
  if (!p) return "Select range";
  if (preset === "custom") {
    if (from && to) {
      try {
        return `${format(new Date(from), "d MMM yy")} – ${format(new Date(to), "d MMM yy")}`;
      } catch {
        return "Custom range";
      }
    }
    return "Custom range";
  }
  return p.label;
};

export default function DateRangePicker({ value, onChange, className = "" }) {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(value?.preset === "custom");
  const [draftFrom, setDraftFrom] = useState(value?.from || "");
  const [draftTo, setDraftTo] = useState(value?.to || "");

  useEffect(() => {
    setCustomMode(value?.preset === "custom");
    setDraftFrom(value?.from || "");
    setDraftTo(value?.to || "");
  }, [value?.preset, value?.from, value?.to]);

  const label = useMemo(
    () => formatLabel(value?.preset, value?.from, value?.to),
    [value?.preset, value?.from, value?.to]
  );

  const pickPreset = (preset) => {
    if (preset === "custom") {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    onChange({ preset });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!draftFrom || !draftTo) return;
    if (draftFrom > draftTo) return;
    onChange({ preset: "custom", from: draftFrom, to: draftTo });
    setOpen(false);
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-9 px-3 justify-between min-w-[220px] font-normal"
            data-testid="date-range-trigger"
          >
            <span className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span>{label}</span>
            </span>
            <ChevronDown className="h-4 w-4 ml-2 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[420px]" align="start">
          <div className="grid grid-cols-2 divide-x">
            {/* Preset list */}
            <div className="p-1 max-h-[320px] overflow-y-auto" data-testid="date-range-presets">
              {PRESETS.map((p) => {
                const active = value?.preset === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => pickPreset(p.value)}
                    className={`w-full text-left text-sm rounded px-3 py-2 hover:bg-zinc-100 ${
                      active ? "bg-zinc-100 font-medium" : ""
                    }`}
                    data-testid={`date-range-preset-${p.value}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Custom side */}
            <div className="p-3 space-y-3">
              {customMode ? (
                <>
                  <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                    Custom range
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-zinc-500">From</label>
                      <Input
                        type="date"
                        value={draftFrom}
                        onChange={(e) => setDraftFrom(e.target.value)}
                        data-testid="date-range-custom-from"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-zinc-500">To</label>
                      <Input
                        type="date"
                        value={draftTo}
                        min={draftFrom || undefined}
                        onChange={(e) => setDraftTo(e.target.value)}
                        data-testid="date-range-custom-to"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCustomMode(false);
                        setDraftFrom(value?.from || "");
                        setDraftTo(value?.to || "");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={applyCustom}
                      disabled={!draftFrom || !draftTo || draftFrom > draftTo}
                      data-testid="date-range-custom-apply"
                    >
                      Apply
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-xs text-zinc-500 leading-relaxed">
                  Pick a preset on the left or choose <b>Custom…</b> to set
                  any range. Reports also show the equivalent prior period
                  for delta comparison.
                </div>
              )}

              {/* Optional: small calendar preview when in custom mode */}
              {customMode && draftFrom && (
                <div className="hidden">
                  <Calendar
                    mode="single"
                    selected={new Date(draftFrom)}
                    onSelect={() => {}}
                  />
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { PRESETS };
